import React from "react";
import {
  Box,
  Card,
  Typography,
  alpha,
  SxProps,
  Theme,
  CircularProgress,
  Alert,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTheme } from "@mui/material";
import { VarianceAnalysisData } from "../hooks/useVarianceAnalysis";

interface VarianceAnalysisChartProps {
  data: VarianceAnalysisData[];
  isLoading?: boolean;
  isError?: boolean;
  error?: string;
}

const cardStyles: SxProps<Theme> = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
};

export const VarianceAnalysisChart: React.FC<VarianceAnalysisChartProps> = ({
  data,
  isLoading = false,
  isError = false,
  error,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ p: 2, flex: 1 }}>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 250,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error || "Failed to load variance analysis"}
        </Alert>
      ) : data.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 250,
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">No data available</Typography>
        </Box>
      ) : (
        <Box sx={{ height: 250, width: "100%" }}>
          <BarChart
            xAxis={[
              {
                scaleType: "band",
                data: data.map((item) => item.month),
                tickLabelStyle: { fontSize: 12 },
                height: 30,
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: { fontSize: 12 },
                valueFormatter: (value: number) =>
                  `£${Math.abs(value).toFixed(1)}M`,
                width: 60,
              },
            ]}
            series={[
              {
                data: data.map((item) => item.actual),
                label: "Actual",
                color: theme.palette.primary.main,
              },
              {
                data: data.map((item) => item.variance),
                label: "Variance",
                color: theme.palette.error.main,
              },
            ]}
            slotProps={{
              legend: {
                position: { vertical: "top", horizontal: "end" },
              },
            }}
            grid={{ vertical: true, horizontal: true }}
            borderRadius={4}
            margin={{ right: 10, left: 10, top: 0, bottom: 0 }}
          />
        </Box>
      )}
    </Box>
  );
};
